import React, { useState, useEffect } from 'react';
import axios from 'axios';
import RestaurantList from './restaurantList';
import RegisterRestaurantModal from './registerRestaurantModal';
import RestaurantDetails from './restaurantDetails';
import Layout from '../../components/Layout';
import { API_ENDPOINTS } from '../../config/api';

const SuperAdminDashboard = () => {
    const [restaurants, setRestaurants] = useState([]);
    const [filteredRestaurants, setFilteredRestaurants] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRestaurant, setSelectedRestaurant] = useState(null);
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [error, setError] = useState(null);

    // Fetch the registered restaurants when the component mounts
    useEffect(() => {
        const fetchRestaurants = async () => {
            try {
                const response = await axios.get(API_ENDPOINTS.RESTAURANTS_ALL);
                setRestaurants(response.data);
                setFilteredRestaurants(response.data);
            } catch (err) {
                setError('Failed to load restaurants');
                console.error(err);
            }
        };

        fetchRestaurants();
    }, []);

    // Filter restaurants based on search query
    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredRestaurants(restaurants);
        } else {
            const filtered = restaurants.filter(restaurant => 
                restaurant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                restaurant.owner.toLowerCase().includes(searchQuery.toLowerCase()) ||
                restaurant.GST.toLowerCase().includes(searchQuery.toLowerCase()) ||
                restaurant.phone.includes(searchQuery)
            );
            setFilteredRestaurants(filtered);
        }
    }, [searchQuery, restaurants]);

    const handleRegister = (newRestaurant) => {
        const updatedRestaurants = [...restaurants, { ...newRestaurant, active: true }];
        setRestaurants(updatedRestaurants);
        setFilteredRestaurants(updatedRestaurants);
        setShowRegisterModal(false);
    };

    // Toggle the restaurant's active/inactive status
    const toggleRestaurantStatus = async (restaurantId, index) => {
        try {
            const response = await axios.put(API_ENDPOINTS.RESTAURANT_TOGGLE_STATUS(restaurantId));
            const updatedRestaurant = response.data;

            const updatedRestaurants = [...restaurants];
            updatedRestaurants[index] = updatedRestaurant;
            setRestaurants(updatedRestaurants);
        } catch (err) {
            setError('Failed to update restaurant status');
            console.error(err);
        }
    };

    // Handle the deletion of a restaurant
    const handleDeleteRestaurant = async (restaurantId, index) => {
        try {
            await axios.delete(API_ENDPOINTS.RESTAURANT_DELETE(restaurantId));
            const updatedRestaurants = [...restaurants];
            updatedRestaurants.splice(index, 1);
            setRestaurants(updatedRestaurants);
            setSelectedRestaurant(null); // Reset selected restaurant after deletion
        } catch (err) {
            setError('Failed to delete restaurant');
            console.error(err);
        }
    };

    // Handle the update of a restaurant
    const handleUpdateRestaurant = async (restaurantId, updatedData) => {
        try {
            const response = await axios.put(API_ENDPOINTS.RESTAURANT_UPDATE(restaurantId), updatedData);
            const updatedRestaurant = response.data;

            const updatedRestaurants = restaurants.map((restaurant) =>
                restaurant._id === restaurantId ? updatedRestaurant : restaurant
            );

            setRestaurants(updatedRestaurants);
            setSelectedRestaurant(null); // Refresh the selected restaurant
        } catch (err) {
            setError('Failed to update restaurant');
            console.error(err);
        }
    };

    // Set selected restaurant when clicking "View Details"
    const handleSelectRestaurant = (restaurant) => {
        setSelectedRestaurant({ ...restaurant }); // Ensure we create a fresh copy to avoid mutation issues
    };

    const activeRestaurants = restaurants.filter(r => r.active).length;
    const inactiveRestaurants = restaurants.length - activeRestaurants;

    return (
        <Layout>
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
                {/* Animated Background Elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
                    <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
                    <div className="absolute top-40 left-1/2 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
                </div>

                {/* Header Section */}
                <div className="relative z-10">
                    <div className="bg-gradient-to-r from-blue-600/90 via-cyan-600/90 to-purple-600/90 backdrop-blur-xl border-b border-white/10 shadow-2xl">
                        <div className="container mx-auto px-6 py-12">
                            <div className="flex flex-col lg:flex-row justify-between items-center">
                                <div className="text-center lg:text-left mb-8 lg:mb-0">
                                    <div className="flex items-center justify-center lg:justify-start gap-4 mb-4">
                                        <div className="relative">
                                            <span className="text-6xl animate-bounce">👑</span>
                                            <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full animate-ping"></div>
                                        </div>
                                        <div>
                                            <h1 className="text-5xl font-bold bg-gradient-to-r from-white via-blue-100 to-cyan-100 bg-clip-text text-transparent">
                                                Super Admin
                                            </h1>
                                            <div className="text-2xl font-semibold text-blue-200 mt-1">Dashboard</div>
                                        </div>
                                    </div>
                                    <p className="text-blue-100 text-xl max-w-md">Command center for restaurant empire management</p>
                                </div>
                                
                                {/* Stats Cards */}
                                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                                    <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6 text-center border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
                                        <div className="text-4xl font-bold text-white">{restaurants.length}</div>
                                        <div className="text-sm text-blue-200 mt-1">Total Restaurants</div>
                                        <div className="text-2xl mt-2">🏪</div>
                                    </div>
                                    <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6 text-center border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
                                        <div className="text-4xl font-bold text-green-300">{activeRestaurants}</div>
                                        <div className="text-sm text-blue-200 mt-1">Active</div>
                                        <div className="text-2xl mt-2">✅</div>
                                    </div>
                                    <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6 text-center border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105 col-span-2 lg:col-span-1">
                                        <div className="text-4xl font-bold text-red-300">{inactiveRestaurants}</div>
                                        <div className="text-sm text-blue-200 mt-1">Inactive</div>
                                        <div className="text-2xl mt-2">⏸️</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="relative z-10 container mx-auto px-6 py-8">
                    {/* Action Bar */}
                    <div className="mb-8 flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <button
                            onClick={() => setShowRegisterModal(true)}
                            className="group bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 hover:from-emerald-600 hover:via-green-600 hover:to-teal-600 text-white px-8 py-4 rounded-3xl font-bold text-lg shadow-2xl hover:shadow-emerald-500/25 transform hover:scale-110 transition-all duration-500 flex items-center gap-3 relative overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                            <span className="text-3xl group-hover:rotate-12 transition-transform duration-300">🏪</span>
                            <span className="relative z-10">Register New Restaurant</span>
                            <span className="text-2xl group-hover:translate-x-1 transition-transform duration-300">✨</span>
                        </button>
                        
                        {selectedRestaurant && (
                            <button
                                onClick={() => setSelectedRestaurant(null)}
                                className="group bg-gradient-to-r from-slate-600 via-gray-600 to-slate-600 hover:from-slate-700 hover:via-gray-700 hover:to-slate-700 text-white px-6 py-3 rounded-2xl font-semibold shadow-xl hover:shadow-slate-500/25 transform hover:scale-105 transition-all duration-300 flex items-center gap-2"
                            >
                                <span className="group-hover:-translate-x-1 transition-transform duration-300">←</span>
                                <span>Back to Overview</span>
                            </button>
                        )}
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="bg-gradient-to-r from-red-500/90 via-pink-500/90 to-red-500/90 backdrop-blur-sm text-white p-6 rounded-3xl mb-8 shadow-2xl border border-red-300/20 flex items-center gap-4">
                            <span className="text-3xl animate-bounce">⚠️</span>
                            <div>
                                <div className="font-bold text-lg">Error Occurred</div>
                                <div className="text-red-100">{error}</div>
                            </div>
                        </div>
                    )}

                    {/* Main Content */}
                    <div className="relative">
                        {!selectedRestaurant ? (
                            /* Restaurant List Only */
                            <div className="transform transition-all duration-500 ease-out">
                                <RestaurantList
                                    restaurants={filteredRestaurants}
                                    toggleStatus={toggleRestaurantStatus}
                                    onSelectRestaurant={handleSelectRestaurant}
                                    onDeleteRestaurant={handleDeleteRestaurant}
                                    searchQuery={searchQuery}
                                    setSearchQuery={setSearchQuery}
                                    filteredCount={filteredRestaurants.length}
                                />
                            </div>
                        ) : (
                            /* Split View: List + Details */
                            <div className="space-y-8 transform transition-all duration-500 ease-out">
                                <div className="grid grid-cols-1 xl:grid-cols-5 gap-8">
                                    {/* Restaurant List - Compact View */}
                                    <div className="xl:col-span-2">
                                        <div className="sticky top-8">
                                            <RestaurantList
                                                restaurants={filteredRestaurants}
                                                toggleStatus={toggleRestaurantStatus}
                                                onSelectRestaurant={handleSelectRestaurant}
                                                onDeleteRestaurant={handleDeleteRestaurant}
                                                searchQuery={searchQuery}
                                                setSearchQuery={setSearchQuery}
                                                filteredCount={filteredRestaurants.length}
                                            />
                                        </div>
                                    </div>

                                    {/* Restaurant Details */}
                                    <div className="xl:col-span-3">
                                        <RestaurantDetails
                                            restaurant={selectedRestaurant}
                                            onUpdateRestaurant={handleUpdateRestaurant}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Modal */}
                {showRegisterModal && (
                    <RegisterRestaurantModal
                        onRegister={handleRegister}
                        onClose={() => setShowRegisterModal(false)}
                    />
                )}
            </div>
        </Layout>
    );
};

export default SuperAdminDashboard;
